/* eslint-disable */
/**
 * DynamicPlanFields
 *
 * Config-driven field renderer, extracted from CreateVEBPlanV2's `renderField`
 * so the Core plan form can render the same field grammar (vebConfig /
 * coreConfig) with the same look and behaviour.
 *
 * Full parity with the VEB engine: meta groups, array, questions (recursive
 * follow-up tree), dropdown, dropdown-with-meta, multiselect chips, file,
 * excelfile, checkbox, date, select and text/number.
 *
 * State lives in the parent — this component only reads `formData` and calls
 * `onChange(path, value)`. Field keys are flat dotted paths ("benefit_amount.spouse.min_age"),
 * matching what the VEB form builds its payload from.
 */
import React, { useCallback } from "react";
import DateFnsUtils from "@date-io/date-fns";
import "date-fns";
import {
	TextField,
	MenuItem,
	Button,
	Box,
	FormControlLabel,
	FormGroup,
	ListItemText,
	Checkbox,
	Switch
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { MuiPickersUtilsProvider, KeyboardDatePicker } from "@material-ui/pickers";
import EventIcon from "@material-ui/icons/Event";
import { Add, Delete } from "@material-ui/icons";
import {
	AnswerType,
	Condition,
	FormField,
	MetaField,
	QuestionNode,
	Routing
} from "../../../../../@types/plan.types";
import { Validation } from "../../../../../@types/validation.types";
import "./veb/VEBPlanForm.css";

export interface DynamicPlanFieldsProps {
	/** Fields to render — already filtered to the current step by the parent. */
	fields: (FormField | MetaField)[];
	/** Flat dotted-path form state. */
	formData: Record<string, any>;
	/** Called with the dotted path and the new value. */
	onChange: (path: string, value: any) => void;
	/** Per-field error messages, keyed by the same dotted path. */
	validation?: Validation;
	/** Options for `optionsKey: "activePlans"`. */
	activePlansNames?: string[];
	/** Options for `optionsKey: "activeRiderPlans"`. */
	activeRiderPlansNames?: string[];
	/** PDF plan document (type: "file"). */
	document?: File | null;
	onDocumentChange?: (file: File | null) => void;
	/** Premium workbook (type: "excelfile"). Omit if the form has no excel step. */
	excelFile?: File | null;
	onExcelFileChange?: (file: File | null) => void;
	/** Group pay frequencies, for `type: "pay-frequency-amounts"`. */
	payFrequencies?: string[];
	/** Options for `optionsKey: "employeeClass"` — the group's employee classes. */
	employeeClassOptions?: string[];
}

const fieldContainerStyles = {
	display: "flex",
	flexWrap: "wrap",
	mb: 1.5,
	p: { xs: 1, sm: 1.5 },
	backgroundColor: "transparent",
	width: "100%"
};

const smallInputStyle = {
	width: "100%",
	maxWidth: { sm: 340 },
	"& .MuiInputBase-input": { padding: "11px 13px" },
	borderRadius: "var(--pf-radius)"
};

const labelGrid = { xs: 12, sm: 4, md: 3 };
const inputGrid = { xs: 12, sm: 8, md: 9 };

const renderLabel = (label?: string, required?: boolean) => (
	<span>
		{label}
		{required && <span style={{ color: "red", marginLeft: 2 }}>*</span>}
	</span>
);

/**
 * Evaluate a field's `condition` against the current form state.
 * Exported so validation can skip fields the user can't actually see —
 * same rules the renderer uses, so the two can't drift apart.
 */
export const isFieldVisible = (field: FormField | MetaField, formData: Record<string, any>): boolean => {
	if (!field.condition) return true;

	const conditions = Array.isArray(field.condition) ? field.condition : [field.condition];

	const evaluate = (cond: Condition): boolean => {
		const currentValue = formData[cond.field];
		let match = false;
		if (cond.includes !== undefined) {
			if (Array.isArray(currentValue)) {
				match = currentValue.includes(cond.includes);
			}
		} else if (cond.value !== undefined) {
			const conditionValues = Array.isArray(cond.value) ? cond.value : [cond.value];
			match = conditionValues.includes(currentValue);
		}
		return cond.not ? !match : match;
	};

	return field.conditionOperator === "OR" ? conditions.some(evaluate) : conditions.every(evaluate);
};

/** Walk a config tree, yielding every visible leaf field with its dotted path. */
export const collectVisibleFields = (
	fields: (FormField | MetaField)[],
	formData: Record<string, any>,
	parentKey?: string
): { field: FormField | MetaField; key: string }[] => {
	const out: { field: FormField | MetaField; key: string }[] = [];

	fields.forEach((field) => {
		if (!isFieldVisible(field, formData)) return;

		const key = parentKey ? `${parentKey}.${field.name}` : field.name ?? "";

		if (field.type === "meta" && field.fields) {
			out.push(...collectVisibleFields(field.fields, formData, key));
		} else {
			out.push({ field, key });
		}
	});

	return out;
};

const DynamicPlanFields = ({
	fields,
	formData,
	onChange,
	validation,
	activePlansNames = [],
	activeRiderPlansNames = [],
	document: planDocument = null,
	onDocumentChange,
	excelFile = null,
	onExcelFileChange,
	payFrequencies = [],
	employeeClassOptions = []
}: DynamicPlanFieldsProps): JSX.Element => {
	const [isDragging, setIsDragging] = React.useState(false);

	const errorFor = (key: string) => validation?.plan?.[key];

	/** Resolve a field's options, preferring a dynamic `optionsKey` list. */
	const optionsFor = useCallback(
		(field: FormField | MetaField): string[] => {
			switch (field.optionsKey) {
				case "activePlans":
					return activePlansNames;
				case "activeRiderPlans":
					return activeRiderPlansNames;
				case "employeeClass":
					return employeeClassOptions;
				default:
					return field.options || [];
			}
		},
		[activePlansNames, activeRiderPlansNames, employeeClassOptions]
	);

	// Enter moves focus to the next input instead of submitting the form.
	const handleFieldKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
		if (e.key !== "Enter") return;
		e.preventDefault();

		const form = (e.target as HTMLElement).closest("form") ?? document;
		const focusable = Array.from(
			form.querySelectorAll<HTMLElement>(
				'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled])'
			)
		).filter((el) => !el.closest('[aria-hidden="true"]'));

		const current = focusable.indexOf(e.target as HTMLElement);
		if (current !== -1 && current < focusable.length - 1) {
			focusable[current + 1].focus();
		}
	}, []);

	const renderField = useCallback(
		(field: FormField | MetaField, parentKey?: string): React.ReactNode => {
			const fieldKey = parentKey ? `${parentKey}.${field.name}` : field.name ?? "";

			// ── CONDITIONAL VISIBILITY ───────────────────────────────────────────
			if (field.condition) {
				const conditions = Array.isArray(field.condition) ? field.condition : [field.condition];

				const evaluateCondition = (cond: Condition): boolean => {
					const currentValue = formData[cond.field];
					let match = false;
					if (cond.includes !== undefined) {
						if (Array.isArray(currentValue)) {
							match = currentValue.includes(cond.includes);
						}
					} else if (cond.value !== undefined) {
						const conditionValues = Array.isArray(cond.value) ? cond.value : [cond.value];
						match = conditionValues.includes(currentValue);
					}
					return cond.not ? !match : match;
				};

				const operator = field.conditionOperator === "OR" ? "OR" : "AND";
				const shouldRender =
					operator === "OR" ? conditions.some(evaluateCondition) : conditions.every(evaluateCondition);

				if (!shouldRender) return null;
			}

			// ── META ─────────────────────────────────────────────────────────────
			if (field.type === "meta" && field.fields) {
				const metaDepth = (parentKey?.match(/\./g) || []).length;
				const depthClass =
					metaDepth === 0 ? "pf-meta" : metaDepth === 1 ? "pf-meta pf-meta--d1" : "pf-meta pf-meta--d2";

				return (
					<Box key={fieldKey} className={depthClass} sx={{ width: "100%", overflow: "hidden" }}>
						{field.label && <div className="pf-meta-title">{field.label}</div>}
						{field.fields.map((subField: any) => renderField(subField, fieldKey))}
					</Box>
				);
			}

			// ── ARRAY ────────────────────────────────────────────────────────────
			if (field.type === "array") {
				const arrayValues: string[] = formData[fieldKey] || [""];

				const handleArrayChange = (index: number, value: string) => {
					const updated = [...arrayValues];
					updated[index] = value;
					onChange(
						fieldKey,
						updated.filter((v) => v.trim() !== "")
					);
				};

				const addItem = () => onChange(fieldKey, [...arrayValues, ""]);

				const removeItem = (index: number) =>
					onChange(
						fieldKey,
						arrayValues.filter((_, i) => i !== index)
					);

				return (
					<Grid
						container
						spacing={2}
						key={fieldKey}
						className="pf-mui-field"
						sx={{ ...fieldContainerStyles, border: "none" }}
					>
						<Grid item {...labelGrid}>
							<div className="pf-label-text">{renderLabel(field.label, field.required)}</div>
						</Grid>
						<Grid item {...inputGrid}>
							{arrayValues.map((val, index) => (
								<Grid
									container
									spacing={1}
									alignItems="center"
									key={index}
									sx={{ mb: 1, flexWrap: "nowrap" }}
								>
									<Grid item xs>
										<TextField
											size="small"
											variant="outlined"
											value={val}
											placeholder={field.placeholder}
											onChange={(e) => handleArrayChange(index, e.target.value)}
											onKeyDown={(e) => {
												handleFieldKeyDown(e);
												if (e.key === "Enter") addItem();
											}}
											fullWidth
											sx={{
												maxWidth: { sm: 280 },
												"& .MuiInputBase-input": { padding: "11px 13px" }
											}}
										/>
									</Grid>
									<Grid item xs="auto">
										<IconButton color="error" onClick={() => removeItem(index)} size="small">
											<Delete fontSize="small" />
										</IconButton>
									</Grid>
								</Grid>
							))}
							<Box sx={{ mt: 1 }}>
								<Button
									variant="contained"
									startIcon={<Add />}
									size="small"
									onClick={addItem}
									sx={{
										backgroundColor: "var(--pf-accent)",
										textTransform: "none",
										boxShadow: "none"
									}}
								>
									Add
								</Button>
							</Box>
							{errorFor(fieldKey) && <div className="pf-helper">{errorFor(fieldKey)}</div>}
						</Grid>
					</Grid>
				);
			}

			// ── CHECKBOX GROUP (e.g. Core employee class) ────────────────────────
			// Vertical checkbox list, same as the Core form renders employee class.
			// Multi-select; value is a string[].
			if (field.type === "checkbox-group") {
				const selected: string[] = Array.isArray(formData[fieldKey]) ? formData[fieldKey] : [];
				const options = optionsFor(field);

				const toggle = (option: string, checked: boolean) =>
					onChange(
						fieldKey,
						checked ? [...selected, option] : selected.filter((v) => v !== option)
					);

				return (
					<Grid container spacing={2} key={fieldKey} className="pf-mui-field" sx={{ ...fieldContainerStyles }}>
						<Grid item {...labelGrid}>
							<div className="pf-label-text">{renderLabel(field.label, field.required)}</div>
						</Grid>
						<Grid item {...inputGrid}>
							{options.length === 0 ? (
								<div className="pf-helper">No options are configured for this group.</div>
							) : (
								<FormGroup>
									{options.map((option) => (
										<FormControlLabel
											key={option}
											control={
												<Checkbox
													size="small"
													checked={selected.includes(option)}
													onChange={(e) => toggle(option, e.target.checked)}
													sx={{ "&.Mui-checked": { color: "var(--pf-accent)" } }}
												/>
											}
											label={option}
											sx={{ "& .MuiTypography-root": { fontSize: 13 } }}
										/>
									))}
								</FormGroup>
							)}
							{errorFor(fieldKey) && <div className="pf-helper">{errorFor(fieldKey)}</div>}
						</Grid>
					</Grid>
				);
			}

			// ── PAY-FREQUENCY AMOUNTS (Core custom contribution) ─────────────────
			// One "Min Limit" input per group pay frequency, exactly as the Core
			// plan form renders it. Value shape: [{ pay_frequency, min_limit }].
			if (field.type === "pay-frequency-amounts") {
				const entries: { pay_frequency: string; min_limit: number }[] = formData[fieldKey] || [];

				const setAmount = (frequency: string, raw: string) => {
					const min_limit = Number(raw);
					const next = [...entries];
					const idx = next.findIndex((e) => e.pay_frequency === frequency);

					if (raw === "") {
						onChange(
							fieldKey,
							next.filter((e) => e.pay_frequency !== frequency)
						);
						return;
					}

					if (idx > -1) next[idx] = { ...next[idx], min_limit };
					else next.push({ pay_frequency: frequency, min_limit });

					onChange(fieldKey, next);
				};

				if (payFrequencies.length === 0) {
					return (
						<Grid container spacing={2} key={fieldKey} className="pf-mui-field" sx={{ ...fieldContainerStyles }}>
							<Grid item {...labelGrid}>
								<div className="pf-label-text">{renderLabel(field.label, field.required)}</div>
							</Grid>
							<Grid item {...inputGrid}>
								<div className="pf-helper">
									No pay frequencies are configured for this group.
								</div>
							</Grid>
						</Grid>
					);
				}

				return (
					<React.Fragment key={fieldKey}>
						{payFrequencies.map((frequency) => {
							const existing = entries.find((e) => e.pay_frequency === frequency);
							const rowKey = `${fieldKey}.${frequency}`;

							return (
								<Grid
									container
									spacing={2}
									key={rowKey}
									className="pf-mui-field"
									sx={{ ...fieldContainerStyles }}
								>
									<Grid item {...labelGrid}>
										<div className="pf-label-text required">
											{frequency.charAt(0) + frequency.slice(1).toLowerCase()}
											&nbsp;Custom Contribution (Min Limit)
										</div>
									</Grid>
									<Grid item {...inputGrid}>
										<TextField
											size="small"
											type="number"
											variant="outlined"
											value={existing?.min_limit ?? ""}
											onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
											onKeyDown={handleFieldKeyDown}
											onChange={(e) => setAmount(frequency, e.target.value)}
											sx={smallInputStyle}
											InputProps={{ style: { height: 44 } }}
										/>
										{errorFor(rowKey) && <div className="pf-helper">{errorFor(rowKey)}</div>}
									</Grid>
								</Grid>
							);
						})}
					</React.Fragment>
				);
			}
			// ── QUESTIONS (recursive follow-up tree) ─────────────────────────────
			if (field.type === "questions") {
				const questions: QuestionNode[] = formData[fieldKey] || [];
				let idSeed = 0;
				const generateId = () => `q_${Date.now()}_${(idSeed++).toString(36)}_${questions.length}`;
				const updateQuestions = (updated: QuestionNode[]) => onChange(fieldKey, updated);

				const updateNode = (tree: QuestionNode[], id: string, patch: Partial<QuestionNode>): QuestionNode[] =>
					tree.map((q) =>
						q.id === id
							? { ...q, ...patch }
							: {
									...q,
									follow_ups: q.follow_ups?.map((fu) => ({
										...fu,
										questions: updateNode(fu.questions, id, patch)
									}))
							  }
					);

				const removeNode = (tree: QuestionNode[], id: string): QuestionNode[] =>
					tree
						.filter((q) => q.id !== id)
						.map((q) => ({
							...q,
							follow_ups: q.follow_ups?.map((fu) => ({ ...fu, questions: removeNode(fu.questions, id) }))
						}));

				const addFollowUp = (tree: QuestionNode[], parentId: string, answer: string): QuestionNode[] =>
					tree.map((q) => {
						if (q.id === parentId) {
							const existing = q.follow_ups?.find((fu) => fu.answer === answer);
							if (existing) return q;
							return {
								...q,
								follow_ups: [
									...(q.follow_ups || []),
									{
										answer,
										routing: "next" as Routing,
										questions: [
											{
												id: generateId(),
												text: "",
												answer_type: "yes_no" as const,
												options: [],
												follow_ups: []
											}
										]
									}
								]
							};
						}
						return {
							...q,
							follow_ups: q.follow_ups?.map((fu) => ({
								...fu,
								questions: addFollowUp(fu.questions, parentId, answer)
							}))
						};
					});

				const updateFollowUpRouting = (
					tree: QuestionNode[],
					parentId: string,
					answer: string,
					routing: Routing
				): QuestionNode[] =>
					tree.map((q) => {
						if (q.id === parentId) {
							return {
								...q,
								follow_ups: q.follow_ups?.map((fu) => (fu.answer === answer ? { ...fu, routing } : fu))
							};
						}
						return {
							...q,
							follow_ups: q.follow_ups?.map((fu) => ({
								...fu,
								questions: updateFollowUpRouting(fu.questions, parentId, answer, routing)
							}))
						};
					});

				const routingOptions: { value: Routing; label: string }[] = [
					{ value: "next", label: "Continue to next step" },
					{ value: "prev", label: "Go back to previous step" },
					{ value: "end", label: "End form" }
				];

				const routingSelectSx = {
					minWidth: 200,
					"& .MuiInputBase-input": { padding: "4px 8px", fontSize: 12 }
				};

				const renderQuestionNode = (
					nodes: QuestionNode[],
					onUpdate: (updated: QuestionNode[]) => void,
					depth = 0
				): React.ReactNode => (
					<Box
						sx={{
							pl: depth > 0 ? 2 : 0,
							borderLeft: depth > 0 ? "2px solid var(--pf-accent-mid)" : "none",
							mt: 1
						}}
					>
						{nodes.map((q, idx) => {
							const answers = q.answer_type === "yes_no" ? ["Yes", "No"] : q.options || [];

							return (
								<Box
									key={q.id}
									sx={{
										mb: 2,
										p: 1.5,
										backgroundColor: depth % 2 === 0 ? "#f9f9f9" : "var(--pf-accent-light)",
										borderRadius: "var(--pf-radius)",
										border: "0.5px solid var(--pf-border)"
									}}
								>
									<Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 1 }}>
										<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
											<Typography
												variant="caption"
												sx={{ color: "var(--pf-text-hint)", minWidth: 24 }}
											>
												{idx + 1}.
											</Typography>

											<TextField
												size="small"
												fullWidth
												multiline
												minRows={1}
												maxRows={4}
												placeholder="Enter question text"
												value={q.text}
												onChange={(e) =>
													onUpdate(updateNode(nodes, q.id, { text: e.target.value }))
												}
												sx={{ flex: 1 }}
											/>

											<TextField
												select
												size="small"
												value={q.answer_type}
												onChange={(e) =>
													onUpdate(
														updateNode(nodes, q.id, {
															answer_type: e.target.value as AnswerType,
															options: [],
															follow_ups: [],
															routing: {},
															eligibility_answer: null
														})
													)
												}
												sx={{ minWidth: 150 }}
											>
												<MenuItem value="yes_no">Yes / No</MenuItem>
												<MenuItem value="multiple_choice">Multiple Choice</MenuItem>
											</TextField>

											<IconButton
												size="small"
												color="error"
												onClick={() => onUpdate(removeNode(nodes, q.id))}
											>
												<Delete fontSize="small" />
											</IconButton>
										</Box>

										<Box sx={{ display: "flex", alignItems: "center", gap: 2, pl: 4 }}>
											<FormControlLabel
												control={
													<Switch
														size="small"
														checked={q.have_to_answer || false}
														onChange={(e) =>
															onUpdate(
																updateNode(nodes, q.id, {
																	have_to_answer: e.target.checked
																})
															)
														}
													/>
												}
												label="Required"
												sx={{ "& .MuiTypography-root": { fontSize: 12 } }}
											/>

											{answers.length > 0 && (
												<TextField
													select
													size="small"
													label="Eligible if"
													value={q.eligibility_answer || ""}
													onChange={(e) =>
														onUpdate(
															updateNode(nodes, q.id, {
																eligibility_answer: e.target.value
															})
														)
													}
													sx={{ minWidth: 180 }}
												>
													{answers.map((ans) => (
														<MenuItem key={ans} value={ans}>
															{ans}
														</MenuItem>
													))}
												</TextField>
											)}
										</Box>
									</Box>

									{q.answer_type === "multiple_choice" && (
										<Box sx={{ pl: 4, mb: 1 }}>
											<Typography
												variant="caption"
												sx={{ color: "var(--pf-text-muted)", mb: 0.5, display: "block" }}
											>
												Options
											</Typography>
											{(q.options || []).map((opt, oi) => (
												<Box
													key={oi}
													sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
												>
													<TextField
														size="small"
														placeholder={`Option ${oi + 1}`}
														value={opt}
														onChange={(e) => {
															const updated = [...(q.options || [])];
															updated[oi] = e.target.value;
															onUpdate(updateNode(nodes, q.id, { options: updated }));
														}}
														sx={{ "& .MuiInputBase-input": { padding: "6px 10px" } }}
													/>
													<IconButton
														size="small"
														color="error"
														onClick={() => {
															const updated = (q.options || []).filter((_, i) => i !== oi);
															onUpdate(updateNode(nodes, q.id, { options: updated }));
														}}
													>
														<Delete fontSize="small" />
													</IconButton>
												</Box>
											))}
											<Button
												size="small"
												startIcon={<Add />}
												onClick={() =>
													onUpdate(
														updateNode(nodes, q.id, { options: [...(q.options || []), ""] })
													)
												}
												sx={{ color: "var(--pf-accent)", textTransform: "none" }}
											>
												Add Option
											</Button>
										</Box>
									)}

									<Box sx={{ pl: 4 }}>
										<Typography
											variant="caption"
											sx={{ color: "var(--pf-text-muted)", mb: 0.5, display: "block" }}
										>
											Follow-up branches
										</Typography>

										{answers.map((ans) => {
											const existingBranch = q.follow_ups?.find((fu) => fu.answer === ans);
											const routingValue = existingBranch
												? existingBranch.routing
												: q.routing?.[ans] ?? "next";

											return (
												<Box key={ans} sx={{ mb: 1.5 }}>
													{existingBranch ? (
														<Box>
															<Typography
																variant="caption"
																sx={{
																	display: "inline-block",
																	mb: 0.5,
																	px: 1,
																	py: 0.25,
																	backgroundColor: "var(--pf-accent-light)",
																	borderRadius: "var(--pf-radius-pill)",
																	color: "var(--pf-accent)",
																	fontWeight: 500
																}}
															>
																If &quot;{ans}&quot; →
															</Typography>
															{renderQuestionNode(
																existingBranch.questions,
																(updatedChildren) =>
																	onUpdate(
																		updateNode(nodes, q.id, {
																			follow_ups: q.follow_ups?.map((fu) =>
																				fu.answer === ans
																					? {
																							...fu,
																							questions: updatedChildren
																					  }
																					: fu
																			)
																		})
																	),
																depth + 1
															)}
														</Box>
													) : (
														<Button
															size="small"
															variant="outlined"
															startIcon={<Add />}
															sx={{
																fontSize: 11,
																py: 0.25,
																textTransform: "none",
																borderColor: "var(--pf-accent-mid)",
																color: "var(--pf-accent)"
															}}
															onClick={() => onUpdate(addFollowUp(nodes, q.id, ans))}
														>
															Add follow-up if &quot;{ans}&quot;
														</Button>
													)}

													<Box
														sx={{
															display: "flex",
															alignItems: "center",
															gap: 1,
															mt: existingBranch ? 1 : 0.5
														}}
													>
														<Typography
															variant="caption"
															sx={{
																px: 1,
																py: 0.25,
																borderRadius: "var(--pf-radius-pill)",
																backgroundColor:
																	ans === "Yes"
																		? "#EAF3DE"
																		: ans === "No"
																		? "#FCEBEB"
																		: "var(--pf-accent-light)",
																color:
																	ans === "Yes"
																		? "#3B6D11"
																		: ans === "No"
																		? "#A32D2D"
																		: "var(--pf-accent)",
																fontWeight: 500,
																fontSize: 11,
																whiteSpace: "nowrap"
															}}
														>
															{ans}
														</Typography>
														<Typography
															variant="caption"
															sx={{ color: "var(--pf-text-hint)", whiteSpace: "nowrap" }}
														>
															{existingBranch ? "after follow-up →" : "→"}
														</Typography>
														<TextField
															select
															size="small"
															value={routingValue}
															onChange={(e) => {
																const val = e.target.value as Routing;
																if (existingBranch) {
																	onUpdate(
																		updateFollowUpRouting(nodes, q.id, ans, val)
																	);
																} else {
																	onUpdate(
																		updateNode(nodes, q.id, {
																			routing: {
																				...(q.routing || {}),
																				[ans]: val
																			}
																		})
																	);
																}
															}}
															sx={routingSelectSx}
														>
															{routingOptions.map((opt) => (
																<MenuItem key={opt.value} value={opt.value}>
																	{opt.label}
																</MenuItem>
															))}
														</TextField>
													</Box>
												</Box>
											);
										})}
									</Box>
								</Box>
							);
						})}

						<Button
							size="small"
							variant="contained"
							startIcon={<Add />}
							onClick={() => {
								const newQ: QuestionNode = {
									id: generateId(),
									text: "",
									answer_type: "yes_no",
									options: [],
									follow_ups: [],
									routing: { Yes: "next", No: "next" }
								};
								onUpdate([...nodes, newQ]);
							}}
							sx={{
								mt: 1,
								backgroundColor: "var(--pf-accent)",
								textTransform: "none",
								boxShadow: "none"
							}}
						>
							{depth === 0 ? "Add Question" : "Add Question at this level"}
						</Button>
					</Box>
				);

				return (
					<Grid
						container
						spacing={2}
						key={fieldKey}
						className="pf-mui-field"
						sx={{ ...fieldContainerStyles, border: "none" }}
					>
						<Grid item {...labelGrid}>
							<div className="pf-label-text">{renderLabel(field.label, field.required)}</div>
						</Grid>
						<Grid item {...inputGrid}>{renderQuestionNode(questions, updateQuestions)}</Grid>
					</Grid>
				);
			}

			// ── DROPDOWN ─────────────────────────────────────────────────────────
			if (field.type === "dropdown") {
				const selectedValues: string[] = formData[fieldKey] || [];
				const options: string[] = optionsFor(field);

				return (
					<Grid container spacing={2} key={fieldKey} className="pf-mui-field" sx={{ ...fieldContainerStyles }}>
						<Grid item {...labelGrid}>
							<div className="pf-label-text">{renderLabel(field.label, field.required)}</div>
						</Grid>
						<Grid item {...inputGrid}>
							<TextField
								select
								size="small"
								value={selectedValues}
								onChange={(e) => onChange(fieldKey, e.target.value)}
								SelectProps={{
									multiple: true,
									MenuProps: { PaperProps: { style: { maxHeight: 200 } } },
									renderValue: (selected) =>
										(selected as string[]).length
											? (selected as string[]).join(", ")
											: "Select options"
								}}
								sx={smallInputStyle}
							>
								{options.map((option) => (
									<MenuItem key={option} value={option}>
										<Checkbox
											checked={selectedValues.includes(option)}
											size="small"
											sx={{
												"&.Mui-checked": { color: "var(--pf-accent)" },
												padding: "4px 8px 4px 0"
											}}
										/>
										<ListItemText primary={option} />
									</MenuItem>
								))}
							</TextField>
						</Grid>
					</Grid>
				);
			}

			// ── DROPDOWN WITH META ───────────────────────────────────────────────
			if (field.type === "dropdown-with-meta") {
				const selectedMeta: { plan: string; required: boolean; same_coverage: boolean }[] =
					formData[fieldKey] || [];

				const options: string[] = optionsFor(field);

				const selectedPlans = selectedMeta.map((m) => m.plan);

				const handlePlanToggle = (plan: string) => {
					const already = selectedMeta.find((m) => m.plan === plan);
					if (already) {
						onChange(
							fieldKey,
							selectedMeta.filter((m) => m.plan !== plan)
						);
					} else {
						onChange(fieldKey, [...selectedMeta, { plan, required: false, same_coverage: false }]);
					}
				};

				const handleMetaChange = (plan: string, key: "required" | "same_coverage", value: boolean) => {
					onChange(
						fieldKey,
						selectedMeta.map((m) => (m.plan === plan ? { ...m, [key]: value } : m))
					);
				};

				return (
					<Grid container spacing={2} key={fieldKey} className="pf-mui-field" sx={{ ...fieldContainerStyles }}>
						<Grid item {...labelGrid}>
							<div className="pf-label-text">{renderLabel(field.label, field.required)}</div>
						</Grid>

						<Grid item {...inputGrid}>
							<TextField
								select
								size="small"
								value={selectedPlans}
								onChange={() => {}} // controlled via MenuItem clicks
								SelectProps={{
									multiple: true,
									MenuProps: { PaperProps: { style: { maxHeight: 200 } } },
									renderValue: (selected) =>
										(selected as string[]).length
											? (selected as string[]).join(", ")
											: "Select plans"
								}}
								sx={smallInputStyle}
							>
								{options.map((option) => (
									<MenuItem key={option} value={option} onClick={() => handlePlanToggle(option)}>
										<Checkbox
											checked={selectedPlans.includes(option)}
											size="small"
											sx={{
												"&.Mui-checked": { color: "var(--pf-accent)" },
												padding: "4px 8px 4px 0"
											}}
										/>
										<ListItemText primary={option} />
									</MenuItem>
								))}
							</TextField>

							{selectedMeta.length > 0 && (
								<Box
									sx={{
										mt: 1.5,
										border: "1px solid var(--pf-border)",
										borderRadius: "var(--pf-radius)",
										overflow: "hidden"
									}}
								>
									<Box
										sx={{
											display: "grid",
											gridTemplateColumns: "1fr 110px 130px",
											alignItems: "center",
											px: 1.5,
											py: 0.75,
											backgroundColor: "var(--pf-accent-light)",
											borderBottom: "1px solid var(--pf-border)"
										}}
									>
										<Typography
											variant="caption"
											sx={{ fontWeight: 600, color: "var(--pf-text-muted)" }}
										>
											Plan
										</Typography>
										<Typography
											variant="caption"
											sx={{
												fontWeight: 600,
												color: "var(--pf-text-muted)",
												textAlign: "center"
											}}
										>
											Required
										</Typography>
										<Typography
											variant="caption"
											sx={{
												fontWeight: 600,
												color: "var(--pf-text-muted)",
												textAlign: "center"
											}}
										>
											Same Coverage
										</Typography>
									</Box>

									{selectedMeta.map((meta, idx) => (
										<Box
											key={meta.plan}
											sx={{
												display: "grid",
												gridTemplateColumns: "1fr 110px 130px",
												alignItems: "center",
												px: 1.5,
												py: 0.75,
												backgroundColor: idx % 2 === 0 ? "#fff" : "var(--pf-accent-light)",
												borderBottom:
													idx < selectedMeta.length - 1 ? "1px solid var(--pf-border)" : "none"
											}}
										>
											<Typography variant="body2" sx={{ fontSize: 13 }}>
												{meta.plan}
											</Typography>

											<Box sx={{ display: "flex", justifyContent: "center" }}>
												<Switch
													size="small"
													checked={meta.required}
													onChange={(e) =>
														handleMetaChange(meta.plan, "required", e.target.checked)
													}
													sx={{
														"& .MuiSwitch-switchBase.Mui-checked": {
															color: "var(--pf-accent)"
														},
														"& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
															backgroundColor: "var(--pf-accent)"
														}
													}}
												/>
											</Box>

											<Box sx={{ display: "flex", justifyContent: "center" }}>
												<Switch
													size="small"
													checked={meta.same_coverage}
													onChange={(e) =>
														handleMetaChange(meta.plan, "same_coverage", e.target.checked)
													}
													sx={{
														"& .MuiSwitch-switchBase.Mui-checked": {
															color: "var(--pf-accent)"
														},
														"& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
															backgroundColor: "var(--pf-accent)"
														}
													}}
												/>
											</Box>
										</Box>
									))}
								</Box>
							)}
						</Grid>
					</Grid>
				);
			}

			// ── MULTISELECT (chips) ──────────────────────────────────────────────
			if (field.type === "multiselect") {
				let selectedValues: string[] = formData[fieldKey] || [];
				let selectedValues2: string[] = [];

				if (fieldKey === "rider.coverage_for" && formData["rider.is_rider_only_for_employee"]) {
					selectedValues2 = ["Employee Only"];
					selectedValues = selectedValues2;
				} else if (fieldKey === "rider.coverage_for" && formData["rider.is_rider_has_same_base_plan_coverage_for"]) {
					selectedValues2 = Array.isArray(formData.coverage_for)
						? formData.coverage_for
						: formData.coverage_for?.options || [];

					selectedValues = selectedValues2;
				}

				let displayOptions = field.options || [];

				// When each relationship can enrol independently, combined coverage
				// tiers stop making sense — narrow to the individual ones.
				if (fieldKey === "coverage_for" && formData["indenpendent_relationship"]) {
					displayOptions = ["Employee Only", "Spouse Only", "Children Only", "Spouse & Children"];
				}
				if (fieldKey === "rider.coverage_for" && formData["rider.indenpendent_relationship"]) {
					displayOptions = ["Employee Only", "Spouse Only", "Children Only"];
				}

				const toggleOption = (option: string) => {
					let updatedValues: string[];

					if (selectedValues.includes(option)) {
						updatedValues = selectedValues.filter((v) => v !== option);
					} else if (
						fieldKey === "rider.coverage_for" &&
						formData["rider.is_rider_has_same_base_plan_coverage_for"]
					) {
						updatedValues = Array.from(new Set([...selectedValues2, option]));
					} else {
						updatedValues = [...selectedValues, option];
					}

					onChange(fieldKey, updatedValues);
				};

				return (
					<Grid container spacing={2} key={fieldKey} className="pf-mui-field" sx={{ ...fieldContainerStyles }}>
						<Grid item {...labelGrid}>
							<div className="pf-label-text">{renderLabel(field.label, field.required)}</div>
						</Grid>

						<Grid item {...inputGrid}>
							<div className="pf-chips">
								{displayOptions.map((option) => (
									<div
										key={option}
										className={`pf-chip ${selectedValues.includes(option) ? "pf-chip--selected" : ""}`}
										onClick={() => toggleOption(option)}
									>
										{option}
									</div>
								))}
							</div>
							{errorFor(fieldKey) && <div className="pf-helper">{errorFor(fieldKey)}</div>}
						</Grid>
					</Grid>
				);
			}

			// ── FILE (plan document, PDF) ────────────────────────────────────────
			if (field.type === "file") {
				const acceptPdf = (f: File | undefined | null) => {
					if (!f) return;
					const ext = f.name.toLowerCase().split(".").pop();
					if (ext === "pdf") {
						onDocumentChange?.(f);
					} else {
						alert("Please upload PDF only");
					}
				};

				return (
					<Grid
						container
						spacing={2}
						key={fieldKey}
						className="pf-mui-field"
						sx={{ ...fieldContainerStyles, border: "none" }}
					>
						<Grid item {...labelGrid}>
							<div className="pf-label-text">{field.label}</div>
						</Grid>

						<Grid item {...inputGrid}>
							<label
								className={`pf-file-zone ${isDragging ? "dragging" : ""}`}
								onDragOver={(e) => {
									e.preventDefault();
									setIsDragging(true);
								}}
								onDragLeave={() => setIsDragging(false)}
								onDrop={(e) => {
									e.preventDefault();
									setIsDragging(false);
									acceptPdf(e.dataTransfer.files?.[0]);
								}}
							>
								<div className="pf-file-zone-icon">↑</div>

								<div className="pf-file-zone-text">
									<span>Choose file</span> or drag and drop
									<br />
									PDF only
								</div>

								<input
									type="file"
									accept=".pdf"
									hidden
									onChange={(e) => acceptPdf(e.target.files?.[0])}
								/>
							</label>

							{planDocument && (
								<div className="pf-file-preview">
									<div className="pf-file-name">{planDocument.name}</div>

									<button
										type="button"
										className="pf-file-remove"
										onClick={() => onDocumentChange?.(null)}
									>
										Remove
									</button>
								</div>
							)}
						</Grid>
					</Grid>
				);
			}

			// ── EXCEL FILE (premium workbook) ────────────────────────────────────
			if (field.type === "excelfile") {
				const acceptExcel = (f: File | undefined | null) => {
					if (!f) return;
					const ext = f.name.toLowerCase().split(".").pop();
					if (ext === "xlsx" || ext === "xls" || ext === "csv") {
						onExcelFileChange?.(f);
					} else {
						alert("Please upload only Excel files (.xlsx, .xls, .csv)");
					}
				};

				return (
					<Grid
						container
						spacing={2}
						key={fieldKey}
						className="pf-mui-field"
						sx={{ ...fieldContainerStyles, border: "none" }}
					>
						<Grid item {...labelGrid}>
							<div className="pf-label-text required">{field.label}</div>
						</Grid>

						<Grid item {...inputGrid}>
							<label
								className="pf-file-zone"
								onDragOver={(e) => e.preventDefault()}
								onDrop={(e) => {
									e.preventDefault();
									acceptExcel(e.dataTransfer.files?.[0]);
								}}
							>
								<div className="pf-file-zone-icon">↑</div>
								<div className="pf-file-zone-text">
									<span>Choose Excel file</span> or drag and drop
									<br />
									.xlsx / .xls / .csv
								</div>

								<input
									type="file"
									accept=".xlsx,.xls,.csv"
									hidden
									onChange={(e) => acceptExcel(e.target.files?.[0])}
								/>
							</label>

							{excelFile && (
								<div className="pf-file-preview">
									<span className="pf-file-name">{excelFile.name}</span>

									<button
										type="button"
										className="pf-remove-btn"
										onClick={() => onExcelFileChange?.(null)}
									>
										Remove
									</button>
								</div>
							)}
						</Grid>
					</Grid>
				);
			}

			// ── CHECKBOX ─────────────────────────────────────────────────────────
			if (field.type === "checkbox") {
				return (
					<Grid container spacing={2} key={fieldKey} className="pf-mui-field" sx={{ ...fieldContainerStyles }}>
						<Grid item {...labelGrid}>
							<div className="pf-label-text">{renderLabel(field.label, field.required)}</div>
						</Grid>
						<Grid item {...inputGrid}>
							<label className="pf-checkbox-wrap">
								<input
									type="checkbox"
									className="pf-checkbox"
									checked={Boolean(formData[fieldKey])}
									onChange={(e) => onChange(fieldKey, e.target.checked)}
								/>
							</label>
							{errorFor(fieldKey) && <div className="pf-helper">{errorFor(fieldKey)}</div>}
						</Grid>
					</Grid>
				);
			}

			// ── DATE ─────────────────────────────────────────────────────────────
			if (field.type === "date") {
				return (
					<Grid container spacing={2} key={fieldKey} className="pf-mui-field" sx={{ ...fieldContainerStyles }}>
						<Grid item {...labelGrid}>
							<div className={`pf-label-text ${field.required ? "required" : ""}`}>{field.label}</div>
						</Grid>
						<Grid item {...inputGrid}>
							<MuiPickersUtilsProvider utils={DateFnsUtils}>
								<KeyboardDatePicker
									fullWidth
									size="small"
									inputVariant="outlined"
									label={field.label}
									format="MM/dd/yyyy"
									value={formData[fieldKey] ? new Date(formData[fieldKey]) : null}
									onChange={(date) =>
										onChange(
											fieldKey,
											date ? `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}` : null
										)
									}
									InputLabelProps={{ shrink: true }}
									keyboardIcon={<EventIcon fontSize="small" style={{ color: "var(--pf-accent)" }} />}
									KeyboardButtonProps={{
										"aria-label": "change date",
										style: { color: "var(--pf-accent)" }
									}}
									style={{ width: "100%", maxWidth: 340, borderRadius: 6 }}
									InputProps={{ style: { height: 44, padding: "11px 13px" } }}
								/>
							</MuiPickersUtilsProvider>
							{errorFor(fieldKey) && <div className="pf-helper">{errorFor(fieldKey)}</div>}
						</Grid>
					</Grid>
				);
			}

			// ── SELECT & TEXT / NUMBER (default) ─────────────────────────────────
			return (
				<Grid container spacing={2} key={fieldKey} className="pf-mui-field" sx={{ ...fieldContainerStyles }}>
					<Grid item {...labelGrid}>
						<div className="pf-label-text">{renderLabel(field.label, field.required)}</div>
					</Grid>
					<Grid item {...inputGrid}>
						{field.type === "select" ? (
							<TextField
								select
								size="small"
								value={formData[fieldKey] || ""}
								variant="outlined"
								onChange={(e) => onChange(fieldKey, e.target.value)}
								sx={smallInputStyle}
								InputProps={{ style: { height: 44 } }}
							>
								{field.options?.map((option) => (
									<MenuItem key={option} value={option}>
										{option}
									</MenuItem>
								))}
							</TextField>
						) : (
							<TextField
								size="small"
								type={field.type === "number" ? "number" : "text"}
								value={formData[fieldKey] ?? field.defaultValue ?? ""}
								onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
								variant="outlined"
								placeholder={field.placeholder}
								onKeyDown={handleFieldKeyDown}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
									const newValue = e.target.value;
									if (field.type === "number" && Number(newValue) < 0) return;
									onChange(fieldKey, newValue);
								}}
								required={field.required}
								sx={smallInputStyle}
								InputProps={{ style: { height: 44 } }}
							/>
						)}
						{errorFor(fieldKey) && <div className="pf-helper">{errorFor(fieldKey)}</div>}
					</Grid>
				</Grid>
			);
		},
		[
			formData,
			onChange,
			validation,
			activePlansNames,
			activeRiderPlansNames,
			planDocument,
			onDocumentChange,
			excelFile,
			onExcelFileChange,
			payFrequencies,
			optionsFor,
			isDragging,
			handleFieldKeyDown
		]
	);

	return <>{fields.map((field) => renderField(field))}</>;
};

export default DynamicPlanFields;
